import {
  Button,
  CrudMode,
  DataDisplay,
  formatDate,
  formatSchoolYear,
  Grid,
  pn,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, Input, Select } from "components";
import {
  StudentPostHighSchool,
  StudentPostHighSchoolEditResource,
  StudentPostHighSchoolViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { collegePageRoute } from "pages/system/utility/colleges/CollegePage/CollegePage";
import { useNavigate } from "react-router-dom";

export const StudentPostHighSchoolPageBody = (props: {
  crudMode: CrudMode;
  postHighSchool: StudentPostHighSchool;
  setPostHighSchool: (value: StudentPostHighSchool) => void;
  viewResource: StudentPostHighSchoolViewResource;
  editResource: StudentPostHighSchoolEditResource;
  validationErrors: ValidationErrors;
}) => {
  const properties = props.viewResource.properties || [];
  const navigate = useNavigate();
  const { breakpointsRef, smallFieldBreakpoints, mediumFieldBreakpoints, largeFieldBreakpoints } =
    useElementBreakpoints();

  const addCollege = () => {
    navigate({
      pathname: `./${collegePageRoute.getPath({ id: 0, crudMode: "edit" })}`,
      search: collegePageRoute.getOptionalParams?.({
        closeOnSave: true,
      }),
    });
  };

  const dateField = () => {
    const propertyName = pn<StudentPostHighSchool>("date");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.postHighSchool.date)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.postHighSchool.date}
        onChange={(value) => {
          props.setPostHighSchool(
            produce(props.postHighSchool, (draft) => {
              draft.date = value;
            })
          );
        }}
      />
    );
  };

  const collegeField = () => {
    const propertyName = pn<StudentPostHighSchool>("collegeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.postHighSchool.college?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.colleges?.find((x) => x.id === props.postHighSchool.collegeId)}
        options={props.editResource.colleges}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setPostHighSchool(
            produce(props.postHighSchool, (draft) => {
              draft.collegeId = value?.id;
            })
          )
        }
        noOptionsText={<Button onClick={addCollege}>+ New College</Button>}
      />
    );
  };

  const statusField = () => {
    const propertyName = pn<StudentPostHighSchool>("statusId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.postHighSchool.status?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.statuses?.find((x) => x.id === props.postHighSchool.statusId)}
        options={props.editResource.statuses}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setPostHighSchool(
            produce(props.postHighSchool, (draft) => {
              draft.statusId = value?.id;
            })
          )
        }
      />
    );
  };

  const noteField = () => {
    const propertyName = pn<StudentPostHighSchool>("note");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.postHighSchool.note}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.postHighSchool.note}
        onChange={(e) =>
          props.setPostHighSchool(
            produce(props.postHighSchool, (draft) => {
              draft.note = e.target.value;
            })
          )
        }
      />
    );
  };

  const yearField = () => {
    const propertyName = pn<StudentPostHighSchool>("year");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatSchoolYear(props.postHighSchool.year)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schoolYears?.find((x) => x.id === props.postHighSchool.year)}
        options={props.editResource.schoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setPostHighSchool(
            produce(props.postHighSchool, (draft) => {
              draft.year = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {yearField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {collegeField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {statusField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {dateField()}
      </Grid>

      <Grid item xs={mediumFieldBreakpoints}>
        {noteField()}
      </Grid>
    </Grid>
  );
};
